import { Property } from 'csstype';
import { type TFunction } from 'i18next';
import { Theme } from '@mui/material';

import { numbersToStringWithCommas } from '../helpers';
import {
  AggregationType,
  ChartType,
  SeriesItem,
} from 'src/services/insight-cards/types';
import { convertDateRangeToString } from 'src/utils/dateUtils';

interface ChartTooltipProps {
  aggregationLabel?: string;
  aggregationType: AggregationType;
  chartType: ChartType;
  color?: string;
  currentPointIndex?: number;
  endTimestamp: number;
  eventName?: string | null;
  funnelConversationsCurrent?: number | undefined | null;
  funnelConversationsTotal?: number | undefined | null;
  isGrouped?: boolean;
  label: string;
  percentage?: number | null;
  percentageBaseline?: number | null;
  sourceItems: SeriesItem[] | null;
  startTimestamp: number;
  t: TFunction<'solve-insights'>;
  theme: Theme;
  totalSum?: number | null;
  value: number;
  valuePrefix?: string | null;
  valueSuffix?: string | null;
}

const getAggregationTypeToLabelMapping = (
  t: TFunction<'solve-insights'>,
): Record<AggregationType, string> => ({
  avg: t('cards.tooltip.labels.average'),
  conversion: t('cards.tooltip.labels.converted'),
  formula: t('cards.tooltip.labels.formula'),
  rate: t('cards.tooltip.labels.rate'),
  sum: t('cards.tooltip.labels.sum'),
  total: t('cards.tooltip.labels.total'),
  unique: t('cards.tooltip.labels.unique'),
});

const getAggregationTypeAggregationLabel = (
  t: TFunction<'solve-insights'>,
): Record<AggregationType, string> => ({
  avg: t('cards.tooltip.aggregationLabels.average'),
  conversion: t('cards.tooltip.aggregationLabels.converted'),
  formula: t('cards.tooltip.aggregationLabels.formula'),
  rate: t('cards.tooltip.aggregationLabels.rate'),
  sum: t('cards.tooltip.aggregationLabels.sum'),
  total: t('cards.tooltip.aggregationLabels.total'),
  unique: t('cards.tooltip.aggregationLabels.unique'),
});

export const ChartTooltip = ({
  aggregationLabel,
  aggregationType,
  chartType,
  color,
  currentPointIndex,
  endTimestamp,
  eventName,
  funnelConversationsCurrent,
  funnelConversationsTotal,
  isGrouped,
  label,
  percentage,
  percentageBaseline,
  sourceItems,
  startTimestamp,
  t,
  theme,
  totalSum,
  value,
  valuePrefix,
  valueSuffix,
}: ChartTooltipProps) => {
  // Format date range
  const startDate = new Date(startTimestamp * 1000);
  const endDate = new Date(endTimestamp * 1000);
  const dateRangeString = convertDateRangeToString({
    from: startDate,
    to: endDate,
  });
  const isSumOrAverage = aggregationType === 'sum' || aggregationType === 'avg';
  const aggregationTypeToLabelMapping = getAggregationTypeToLabelMapping(t);
  const aggregationTypeAggregationLabel = getAggregationTypeAggregationLabel(t);

  const ConversionMetricCards = () => {
    // Conversion aggregation type only applies to bar charts
    return (
      <>
        <Metric
          label={aggregationTypeToLabelMapping[aggregationType]}
          theme={theme}
          value={percentageBaseline ?? (value || 0)}
          variant='percent'
        />
        <Metric
          formattedValue={`${numbersToStringWithCommas({
            number: funnelConversationsCurrent || 0,
            style: 'decimal',
          })}/${numbersToStringWithCommas({
            number: funnelConversationsTotal || 0,
            style: 'decimal',
          })}`}
          label={t('cards.tooltip.conversations')}
          theme={theme}
          variant='value'
        />
        {currentPointIndex !== 0 && (
          <Metric
            fontWeight={400}
            label={t('cards.tooltip.fromPreviousStep')}
            theme={theme}
            value={percentage ?? (value || 0)}
            variant='percent'
          />
        )}
      </>
    );
  };

  const MetricCards = () => {
    switch (chartType) {
      case 'pie':
        return (
          <>
            <Metric
              label={aggregationTypeToLabelMapping[aggregationType]}
              theme={theme}
              value={value || 0}
              valuePrefix={valuePrefix}
              valueSuffix={valueSuffix}
              variant='value'
            />
            {percentage !== null && percentage !== undefined && (
              <Metric
                label={t('cards.tooltip.ofTotal')}
                theme={theme}
                value={percentage}
                variant='percent'
              />
            )}
            <Metric
              label={t('cards.tooltip.total')}
              theme={theme}
              value={totalSum || 0}
              valuePrefix={valuePrefix}
              valueSuffix={valueSuffix}
              variant='value'
            />
          </>
        );
      default:
        return (
          <>
            <Metric
              label={aggregationTypeToLabelMapping[aggregationType]}
              theme={theme}
              value={value || 0}
              valuePrefix={valuePrefix}
              valueSuffix={valueSuffix}
              variant='value'
            />
            {typeof percentageBaseline === 'number' && (
              <Metric
                color={
                  percentageBaseline === 0
                    ? theme.palette.text.primary
                    : percentageBaseline > 0
                      ? theme.palette.colors.green[500]
                      : theme.palette.colors.red[500]
                }
                label={t('cards.tooltip.fromControl')}
                theme={theme}
                value={percentageBaseline}
                valuePrefix={valuePrefix ?? (percentageBaseline > 0 ? '+' : '')}
                valueSuffix={valueSuffix ?? '%'}
                variant='value'
              />
            )}
          </>
        );
    }
  };

  return (
    <div
      style={{
        color: theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minWidth: '200px',
        padding: '8px',
      }}
    >
      <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
        {color && (
          <div
            style={{
              backgroundColor: color,
              borderRadius: '2px',
              height: '12px',
              width: '12px',
            }}
          />
        )}
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {label}{' '}
          {isSumOrAverage && !isGrouped
            ? t('cards.tooltip.dashValue', { value: aggregationLabel })
            : ''}
        </div>
      </div>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 400,
        }}
      >
        {aggregationTypeAggregationLabel[aggregationType]}
        {isGrouped && eventName
          ? `${t('cards.tooltip.ofValue', { value: eventName })}${
              isSumOrAverage
                ? t('cards.tooltip.dashValue', { value: aggregationLabel })
                : ''
            }`
          : isSumOrAverage && aggregationLabel && !eventName
            ? t('cards.tooltip.ofValue', { value: aggregationLabel })
            : ''}
      </div>
      <div
        style={{
          color: theme.palette.text.secondary,
          fontSize: '12px',
        }}
      >
        {dateRangeString}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {sourceItems && sourceItems.length > 0 ? (
          <SourceItems
            percentage={percentage}
            sourceItems={sourceItems}
            t={t}
            theme={theme}
            value={value}
          />
        ) : aggregationType === 'conversion' ? (
          <ConversionMetricCards />
        ) : (
          <MetricCards />
        )}
      </div>
    </div>
  );
};

const Metric = ({
  color,
  fontWeight = 600,
  formattedValue,
  label,
  theme,
  value = 0,
  valuePrefix,
  valueSuffix,
  variant,
}: {
  fontWeight?: number;
  formattedValue?: string;
  label: string;
  theme: Theme;
  value?: number;
  valuePrefix?: string | null;
  valueSuffix?: string | null;
  variant: 'percent' | 'value';
  color?: Property.Color | undefined;
}) => {
  const baseFormattedValue =
    formattedValue ||
    numbersToStringWithCommas({
      number: variant === 'percent' ? value / 100 : value,
      style: variant === 'percent' ? 'percent' : 'decimal',
    });

  const currentFormattedValue =
    (valuePrefix || '') + baseFormattedValue + (valueSuffix || '');

  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      <div
        style={{
          color: color,
          fontSize: '14px',
          fontWeight,
        }}
      >
        {currentFormattedValue}
      </div>
      <div style={{ color: theme.palette.text.secondary, fontSize: '14px' }}>
        {label}
      </div>
    </div>
  );
};

function SourceItems({
  percentage,
  sourceItems,
  t,
  value,
}: {
  percentage: number | null | undefined;
  sourceItems: SeriesItem[] | null;
  t: TFunction<'solve-insights'>;
  theme: Theme;
  value: number;
}) {
  return (
    <table
      style={{ borderCollapse: 'collapse', fontSize: '14px', width: '100%' }}
    >
      <tbody>
        {sourceItems?.map((item, index) => (
          <tr key={index}>
            <td style={{ fontWeight: '600' }}>{item.label}</td>
            <td style={{ fontWeight: '600', textAlign: 'right' }}>
              {numbersToStringWithCommas({
                number: item.data,
                style: 'decimal',
              })}
            </td>
            <td style={{ textAlign: 'right' }}>
              {numbersToStringWithCommas({
                number: item.percentage / 100,
                style: 'percent',
              })}
            </td>
          </tr>
        ))}
        <tr>
          <td style={{ fontWeight: '600' }}>{t('cards.tooltip.totalRow')}</td>
          <td style={{ fontWeight: '600', textAlign: 'right' }}>
            {numbersToStringWithCommas({
              number: value,
              style: 'decimal',
            })}
          </td>
          <td style={{ textAlign: 'right' }}>
            {percentage !== null && percentage !== undefined
              ? numbersToStringWithCommas({
                  number: percentage / 100,
                  style: 'percent',
                })
              : '—'}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
